import { RemoteRecorderLogs } from './remote-recorder-logs';
import { Injectable, OnDestroy } from '@angular/core';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { BehaviorSubject, Subject } from 'rxjs';
import { BnetStatsFetcher, BnetMatchContext, BnetStatsUpdate } from './bnet-stats-fetcher';
import { environment, Parse } from '@app/data/common-imports';
import { NodeService } from './node.service';
import { ObserverRelayService } from './observer-relay.service';
import {
    RecorderMessageType,
    RecorderRuntimeEvent,
    RecorderRuntimeStartOptions,
    RecorderStateKey
} from './recorder-runtime.types';

interface RecorderHostMessage {
    type: RecorderMessageType;
    message: string;
}

interface RecorderHostSnapshot {
    state: RecorderStateKey | null;
    activeMatchup: string | null;
    messages: RecorderHostMessage[];
}

@Injectable({ providedIn: 'root' })
export class RecorderRuntimeService implements OnDestroy {
    private readonly eventSubject = new Subject<RecorderRuntimeEvent>();
    private readonly logSubject = new Subject<string>();
    private readonly stateSubject = new BehaviorSubject<RecorderStateKey>(RecorderStateKey.INITIALIZING);
    private startPromise: Promise<void> | null = null;
    private startOptions: RecorderRuntimeStartOptions | null = null;
    private startupPhase = 'not-started';
    private phaseAt = Date.now();
    private stateAt = Date.now();
    private lastMessageAt = 0;
    private gameTime: number | null = null;
    private masterReconnects = 0;
    private masterDroppedMessages = 0;
    private diagnosticPending: Promise<any> | null = null;
    private diagnosticSampling = false;
    private matchUpdateWss: ReconnectingWebSocket | null = null;
    private activeLocalMatchup: string | null = null;
    private lastRecorderState: string | null = null;
    private readonly applicationData = new Map<string, Record<string, any>>();
    private activeBnetMatch: any = null;
    private bnetContext: BnetMatchContext | null = null;
    private readonly bnetStats = new Map<string, BnetStatsUpdate>();
    private readonly remoteLogs = new RemoteRecorderLogs(
        (method, input) => Parse.Cloud.run(method, input ? { input } : {}),
        () => Parse.User.current()?.id,
        () => { void this.sampleDiagnostics(); },
        () => this.stopDiagnostics()
    );
    private readonly bnetFetcher = new BnetStatsFetcher(update => {
        if (update.contextId !== this.activeBnetMatch?.statsContext) return;
        // A reconnect refetch may fail transiently; retain verified same-match stats.
        if (update.source === 'cache' && this.bnetStats.get(update.playerId)?.records.length &&
            this.bnetStats.get(update.playerId)?.source !== 'cache') return;
        if (!update.records.length && update.source !== 'live' && this.bnetStats.get(update.playerId)?.records.length) return;
        this.bnetStats.set(update.playerId, update);
        this.sendToMaster(RecorderMessageType.BNET_STATS, update);
    }, undefined, event => {
        const message = '[Battle.net stats] ' + JSON.stringify(event);
        this.logSubject.next(message);
        this.remoteLogs.record('info', 'bnet', message, String(this.activeBnetMatch?.id || ''));
    }, details => this.remoteLogs.record('verbose', 'bnet', 'WC3 roster comparison', String(this.activeBnetMatch?.id || ''), details),
        event => this.remoteLogs.recordBnetWire(event), () => this.remoteLogs.enabled);
    private processing = Promise.resolve();
    private readonly hostMessageListener = (_event, payload: RecorderHostMessage) => this.handleHostMessage(payload);
    private readonly hostLogListener = (_event, message: string) => {
        this.logSubject.next(String(message));
        this.remoteLogs.record('info', 'recorder', String(message), String(this.activeBnetMatch?.id || ''));
    };
    private readonly hostDiagnosticListener = (_event, details: any) => {
        if (typeof details?.event === 'string') this.remoteLogs.diagnostic(details.event, details, this.diagnosticMatchId());
    };
    private readonly errorListener = (event: ErrorEvent) => this.remoteLogs.diagnostic('renderer-error',
        { error: event.message || 'Resource failed to load', stack: event.error?.stack });
    private readonly rejectionListener = (event: PromiseRejectionEvent) => this.remoteLogs.diagnostic('renderer-rejection',
        { error: String(event.reason?.message || event.reason), stack: event.reason?.stack });
    private readonly onlineListener = () => this.remoteLogs.diagnostic('network-change', { online: navigator.onLine });
    private readonly beforeUnloadListener = () => {
        this.remoteLogs.diagnostic('renderer-unload'); void this.remoteLogs.flushNow(); this.closeRendererRuntime();
    };

    readonly events = this.eventSubject.asObservable();
    readonly logs = this.logSubject.asObservable();
    readonly states = this.stateSubject.asObservable();

    constructor(
        private readonly node: NodeService,
        private readonly observerRelay: ObserverRelayService
    ) {
        if (this.node.isWindowsHost()) {
            this.node.ipcRenderer.on('recorder-runtime:message', this.hostMessageListener);
            this.node.ipcRenderer.on('recorder-runtime:log', this.hostLogListener);
            this.node.ipcRenderer.on('recorder-runtime:diagnostic', this.hostDiagnosticListener);
        }
        window.addEventListener('beforeunload', this.beforeUnloadListener);
        window.addEventListener('error', this.errorListener);
        window.addEventListener('unhandledrejection', this.rejectionListener);
        window.addEventListener('online', this.onlineListener);
        window.addEventListener('offline', this.onlineListener);
    }

    ngOnDestroy(): void {
        // Dispose renderer-only listeners and sockets; the native main-process
        // host deliberately remains alive for the replacement Angular runtime.
        window.removeEventListener('beforeunload', this.beforeUnloadListener);
        window.removeEventListener('error', this.errorListener);
        window.removeEventListener('unhandledrejection', this.rejectionListener);
        window.removeEventListener('online', this.onlineListener);
        window.removeEventListener('offline', this.onlineListener);
        if (this.node.isWindowsHost()) {
            this.node.ipcRenderer.removeListener('recorder-runtime:message', this.hostMessageListener);
            this.node.ipcRenderer.removeListener('recorder-runtime:log', this.hostLogListener);
            this.node.ipcRenderer.removeListener('recorder-runtime:diagnostic', this.hostDiagnosticListener);
        }
        this.closeRendererRuntime();
    }

    start(options: RecorderRuntimeStartOptions): Promise<void> {
        if (this.startOptions && this.startOptions.userId !== options.userId) {
            this.applicationData.clear(); this.bnetStats.clear(); this.bnetFetcher.stop(); this.bnetFetcher.clearCache();
        }
        if (this.node.isWindowsHost()) this.remoteLogs.start(options.userId);
        this.setPro(options.isPro);
        if (this.startPromise && this.startOptions &&
            (this.startOptions.userId !== options.userId || this.startOptions.localBackend !== options.localBackend)) {
            this.matchUpdateWss?.close();
            this.connectToMaster(options.userId, options.localBackend);
        }
        this.startOptions = { ...options };
        this.startPromise ??= this.startRuntime(options).catch(error => {
            const message = `[Recorder startup] Failed during ${this.startupPhase}: ${error?.message || String(error)}`;
            this.phase('failed');
            this.logSubject.next(message);
            this.remoteLogs.record('info', 'recorder', message);
            this.startPromise = null;
            throw error;
        });
        return this.startPromise;
    }

    setApplicationData(clientId: string, data: Record<string, any>): void {
        const serialized = JSON.stringify(data);
        if (JSON.stringify(this.applicationData.get(clientId)) === serialized) return;
        this.applicationData.set(clientId, JSON.parse(serialized));
        this.sendToMaster(RecorderMessageType.APPLICATION_DATA, { clientId, data });
    }

    setPro(isPro: boolean): void {
        if (this.node.isWindowsHost()) this.node.ipcRenderer.send('recorder-runtime:set-pro', isPro);
    }

    getWarcraftWindow(): Promise<number> {
        if (!this.node.isWindowsHost()) return Promise.reject(new Error('The Warcraft recorder is unavailable.'));
        return this.node.ipcRenderer.invoke('recorder-runtime:get-warcraft-window');
    }

    private async startRuntime(options: RecorderRuntimeStartOptions): Promise<void> {
        this.phase('observer-relay');
        const relay = await this.observerRelay.ensure();
        this.activeLocalMatchup = relay.activeMatchup;
        this.phase('master-connection');
        this.connectToMaster(options.userId, options.localBackend);
        this.phase('native-host');
        if (this.node.isWindowsHost()) await this.startNativeRecorder(options.isPro);
        this.phase('ready');
        this.remoteLogs.record('info', 'recorder', `[Recorder startup] Host attached; nativeState=${this.stateSubject.value}`);
    }

    private async startNativeRecorder(isPro: boolean): Promise<void> {
        const snapshot = await this.node.ipcRenderer.invoke('recorder-runtime:ensure', { isPro });
        if (!isRecorderHostSnapshot(snapshot)) throw new Error('Invalid main-process recorder response.');
        this.activeLocalMatchup = snapshot.activeMatchup;
        for (const message of snapshot.messages) this.handleHostMessage(message);
        await this.processing;
        this.lastRecorderState = snapshot.state === null ? null : String(snapshot.state);
        if (snapshot.state !== null) this.stateSubject.next(snapshot.state);
        // The socket can open before IPC completes. Restore state for either
        // completion order, and again when a current connection reconnects.
        this.rehydrateMaster();
    }

    private handleHostMessage(payload: RecorderHostMessage): void {
        if (!payload || typeof payload.message !== 'string') return;
        this.lastMessageAt = Date.now();
        this.processing = this.processing
            .then(() => this.processIncomingMessage(payload.type, payload.message))
            .catch(error => { this.remoteLogs.diagnostic('host-message-error', { error: error?.message, stack: error?.stack }); console.warn('Recorder host message issue:', error); });
    }

    private connectToMaster(userId: string, localBackend: boolean): void {
        this.matchUpdateWss?.close();
        const masterUrl = new URL('client', localBackend ? 'wss://localhost:25081/' : environment.LIVESERVER_URL);
        masterUrl.searchParams.set('userId', userId);
        this.matchUpdateWss = new ReconnectingWebSocket(masterUrl.toString());
        const socket = this.matchUpdateWss;
        socket.onopen = () => {
            if (socket !== this.matchUpdateWss) return;
            this.masterReconnects++; this.remoteLogs.diagnostic('master-open', { count: this.masterReconnects });
            this.sendToMaster(RecorderMessageType.AUTHENTICATE, { recorderProtocol: 2, sessionToken: Parse.User.current()?.getSessionToken() });
            // Rehydrate every new master connection. This is safe and idempotent,
            // and does not depend on the Angular component having seen a prior open.
            this.rehydrateMaster();
            if (this.activeBnetMatch && this.bnetContext) {
                this.bnetFetcher.stop(); this.bnetFetcher.start(this.activeBnetMatch, this.bnetContext);
            }
        };
        socket.onerror = error => {
            if (socket === this.matchUpdateWss) this.remoteLogs.diagnostic('master-error');
            console.warn('Recorder master connection issue:', error);
        };
        socket.onclose = event => {
            if (socket === this.matchUpdateWss) this.remoteLogs.diagnostic('master-close', { code: event.code, error: event.reason });
        };
    }

    private rehydrateMaster(): void {
        if (this.matchUpdateWss?.readyState !== 1) return;
        if (this.activeLocalMatchup) this.sendToMaster(RecorderMessageType.LOCAL_MATCHUP, this.activeLocalMatchup);
        if (this.lastRecorderState !== null) this.sendToMaster(RecorderMessageType.RECORDER_STATE, this.lastRecorderState);
        for (const update of this.bnetStats.values()) this.sendToMaster(RecorderMessageType.BNET_STATS, update);
        for (const [clientId, data] of this.applicationData) {
            this.sendToMaster(RecorderMessageType.APPLICATION_DATA, { clientId, data });
        }
    }

    private async processIncomingMessage(type: RecorderMessageType, message: string): Promise<void> {
        if (type === RecorderMessageType.BNET_CONTEXT) {
            this.bnetContext = JSON.parse(message);
            this.bnetFetcher.start(this.activeBnetMatch, this.bnetContext);
            return; // Session GUID/ports must never reach the server or overlays.
        }
        if (type === RecorderMessageType.REQUEST_ADMIN) {
            this.node.ipcRenderer.sendSync('request-admin-privileges');
            return;
        }
        if (type === RecorderMessageType.MATCHUP) {
            const data = JSON.parse(message);
            // Regular games send the setup through MATCHUP; LOCAL_MATCHUP is
            // only guaranteed for observer/replay games. Retain the initial
            // native setup for backend reconnects, never the terminal receipt.
            if (data?.class === 'W3Game' && !Object.prototype.hasOwnProperty.call(data, 'isWon')) {
                this.activeLocalMatchup = JSON.stringify([data]);
                if (data.statsContext !== this.activeBnetMatch?.statsContext || String(data.id) !== String(this.activeBnetMatch?.id)) {
                    this.bnetFetcher.stop(); this.bnetStats.clear();
                }
                this.activeBnetMatch = data;
                if (this.bnetContext) this.bnetFetcher.start(data, this.bnetContext);
            }
            this.eventSubject.next({ type: 'matchup', data });
            return;
        }
        if (type === RecorderMessageType.LOCAL_GAMEDATA) {
            return;
        }
        if (type === RecorderMessageType.GAMETIME) {
            const seconds = Number(message);
            this.gameTime = Number.isFinite(seconds) ? seconds : null;
            this.persistGameTime(seconds);
            this.eventSubject.next({ type: 'gametime', seconds });
            return;
        }
        if (type === RecorderMessageType.LOCAL_MATCHUP) {
            this.activeLocalMatchup = message;
        }
        if (type === RecorderMessageType.RECORDER_STATE) {
            const state = Number(message) as RecorderStateKey;
            if (this.lastRecorderState !== message) { this.stateAt = Date.now(); this.remoteLogs.record('info', 'recorder', `[Recorder state] ${RecorderStateKey[state] || state} (${state})`);
                this.remoteLogs.diagnostic('native-state', { nativeState: state, nativeStateName: RecorderStateKey[state] }, this.diagnosticMatchId()); }
            this.lastRecorderState = message;
            this.stateSubject.next(state);
            if (state < RecorderStateKey.GAME_STARTING || state >= RecorderStateKey.GAME_ENDED) {
                this.gameTime = null;
                this.activeLocalMatchup = null;
                this.activeBnetMatch = null; this.bnetContext = null; this.bnetStats.clear(); this.bnetFetcher.stop();
            }
            if (state === RecorderStateKey.WAITING_FOR_GAME || state === RecorderStateKey.GAME_ENDED) {
                this.persistGameTime('');
            }
        }
        if (type !== RecorderMessageType.PING) this.sendToMaster(type, message);
    }

    private sendToMaster(type: RecorderMessageType, data: any): void {
        if (this.matchUpdateWss?.readyState !== 1) { this.masterDroppedMessages++; return; }
        this.matchUpdateWss.send(JSON.stringify({ data, type }));
    }

    private persistGameTime(gameTime: number | ''): void {
        if (!this.node.isAvailable()) return;
        let formatted = '';
        if (gameTime !== '') {
            const minutes = Math.floor(gameTime / 60);
            const seconds = gameTime % 60;
            formatted = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        }
        const workingDir = this.ensureSiblingDirectory('embed');
        this.node.fs.writeFileSync(this.node.path.join(workingDir, 'gametime.txt'), formatted);
    }

    private ensureSiblingDirectory(name: string): string {
        const appWorkingDir = this.node.ipcRenderer.sendSync('get-working-dir');
        const workingDir = this.node.path.join(this.node.path.dirname(appWorkingDir), name);
        if (!this.node.fs.existsSync(workingDir)) this.node.fs.mkdirSync(workingDir);
        return workingDir;
    }

    private phase(value: string): void { this.startupPhase = value; this.phaseAt = Date.now(); }
    private diagnosticGame(): any {
        try { const values = JSON.parse(this.activeLocalMatchup || '[]');
            return (Array.isArray(values) ? values.find(v => v?.class === 'W3Game') : values) || this.activeBnetMatch || {};
        } catch { return this.activeBnetMatch || {}; }
    }
    private diagnosticMatchId(): string { return String(this.diagnosticGame().id || ''); }
    private stopDiagnostics(): void {
        if (this.node.isWindowsHost()) void this.node.ipcRenderer.invoke('recorder-runtime:diagnostics', false).catch(() => {});
    }
    private async sampleDiagnostics(): Promise<void> {
        const capture = this.remoteLogs.captureId;
        if (!capture || this.diagnosticSampling) return;
        this.diagnosticSampling = true;
        let timeout: ReturnType<typeof setTimeout>;
        try {
            this.diagnosticPending ??= this.node.ipcRenderer.invoke('recorder-runtime:diagnostics', true)
                .finally(() => { this.diagnosticPending = null; });
            let host: any;
            try {
                host = await Promise.race([this.diagnosticPending, new Promise((_resolve, reject) => {
                    timeout = setTimeout(() => reject(new Error('Desktop diagnostic request timed out')), 5000);
                })]);
            } catch (error) { host = { hostStatus: 'diagnostics-unavailable', error: error?.message }; }
            if (this.remoteLogs.captureId !== capture) return;
            const game = this.diagnosticGame();
            const release = Array.from(document.scripts).map(s => s.src.match(/\/releases\/([a-f0-9]{40})\//)?.[1]).find(Boolean) || 'unversioned';
            this.remoteLogs.record('info', 'recorder', '[Runtime] Health snapshot', this.diagnosticMatchId(), {
                ...host, hostNativeState: host?.nativeState, kind: 'runtime-health', clientVersion: environment.version, rendererRelease: release,
                phase: this.startupPhase, phaseAgeMs: Date.now() - this.phaseAt,
                nativeState: this.stateSubject.value, nativeStateName: RecorderStateKey[this.stateSubject.value], stateAgeMs: Date.now() - this.stateAt,
                lastMessageAgeMs: this.lastMessageAt ? Date.now() - this.lastMessageAt : null, gameTime: this.gameTime,
                isReplay: game.isReplay, isObserver: game.isObserver, realm: game.realm,
                masterState: this.matchUpdateWss?.readyState, masterReconnects: this.masterReconnects,
                masterDroppedMessages: this.masterDroppedMessages, visible: document.visibilityState === 'visible', online: navigator.onLine,
                ...this.remoteLogs.health()
            });
        } catch { /* Health reporting must never interrupt recording. */ }
        finally { clearTimeout(timeout); this.diagnosticSampling = false; }
    }

    private closeRendererRuntime(): void {
        this.remoteLogs.stop();
        this.bnetFetcher.stop(); this.bnetFetcher.clearCache();
        this.matchUpdateWss?.close();
        this.matchUpdateWss = null;
    }
}

function isRecorderHostSnapshot(value: any): value is RecorderHostSnapshot {
    return !!value && (value.activeMatchup === null || typeof value.activeMatchup === 'string') &&
        (value.state === null || (Number.isInteger(value.state) &&
            value.state >= RecorderStateKey.UNEXPECTED_EXCEPTION && value.state <= RecorderStateKey.UNSUPPORTED_W3_VERSION)) &&
        Array.isArray(value.messages) && value.messages.every(message =>
            !!message && Number.isInteger(message.type) && typeof message.message === 'string');
}
